/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {FormNumberField} from '@ion/dls/src/components/forms/FormNumberField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {DeleteIcon} from '@ion/dls/src/icons/DeleteIcon';
import Render from '@ion/dls/src/render/Render';
import {
  CHECKIN_CONDITION_MIN_VALUE,
  CheckinConditionCombinator,
  CheckinConditionDraftRow,
  CheckinConditionType,
  getCheckinConditionValueErrorKey,
  isVehicleDiscoveryCondition
} from './CanRequestCheckinConditionUtils';

export interface CanRequestCheckinConditionRowProps {
  row: CheckinConditionDraftRow;
  isLast: boolean;
  disabled: boolean;
  typeLabel: string;
  removeConditionLabel: string;
  combinatorLabel: string;
  typeOptions: Array<{label: string; value: string}>;
  conditionValueLabels: Record<CheckinConditionType, string>;
  vehicleDiscoveryStateOptions: Array<{label: string; value: string}>;
  combinatorOptions: Array<{label: string; value: CheckinConditionCombinator}>;
  combinatorValue?: CheckinConditionCombinator;
  onTypeChange: (rowId: string, nextType: CheckinConditionType) => void;
  onValueChange: (rowId: string, nextValue: string) => void;
  onRemove: (rowId: string) => void;
  onCombinatorChange: (index: number, nextCombinator: CheckinConditionCombinator) => void;
  index: number;
}

const useStyles = makeStyles(() => ({
  row : {
    display      : 'flex',
    alignItems   : 'flex-start',
    gap          : '12px',
    marginBottom : '12px'
  },
  typeField : {
    width      : 200,
    flexShrink : 0
  },
  valueField : {
    width      : 260,
    flexShrink : 0
  },
  removeButton : {
    marginTop : '36px'
  },
  combinatorRow : {
    display        : 'flex',
    justifyContent : 'flex-start',
    marginBottom   : '12px'
  },
  combinatorField : {
    width : 140
  }
}));

/**
 * Renders one check-in condition row (type dropdown, value field, remove
 * button) and, unless it's the last row, the AND/OR combinator dropdown for
 * the gap that follows it.
 *
 * ```typescript
 * <CanRequestCheckinConditionRow row={row}
 *   index={index}
 *   isLast={index === rows.length - 1}
 *   disabled={disabled}
 *   typeLabel={typeLabel}
 *   removeConditionLabel={removeCondition}
 *   combinatorLabel={combinatorLabel}
 *   typeOptions={typeOptions}
 *   conditionValueLabels={conditionValueLabels}
 *   vehicleDiscoveryStateOptions={vehicleDiscoveryStateOptions}
 *   combinatorOptions={combinatorOptions}
 *   combinatorValue={combinators[index]}
 *   onTypeChange={onConditionTypeChange}
 *   onValueChange={onConditionValueChange}
 *   onRemove={onRemoveCondition}
 *   onCombinatorChange={onCombinatorChange} />
 * ```
 *
 * @param   row                          The check-in condition draft row to render.
 * @param   index                        The row's position, used to key its trailing combinator gap.
 * @param   isLast                       Hides the trailing combinator dropdown when true.
 * @param   disabled                     Disables every control when true.
 * @param   typeLabel                    The condition type field's label.
 * @param   removeConditionLabel         The remove button's accessible label.
 * @param   combinatorLabel              The combinator dropdown's label.
 * @param   typeOptions                  The condition types still available for this row.
 * @param   conditionValueLabels         The value field label per condition type.
 * @param   vehicleDiscoveryStateOptions The Vehicle Discovery state dropdown options.
 * @param   combinatorOptions            The AND/OR dropdown options.
 * @param   combinatorValue              The combinator before this row's trailing gap.
 * @param   onTypeChange                 Invoked with the row id and next type on type change.
 * @param   onValueChange                Invoked with the row id and next value on value change.
 * @param   onRemove                     Invoked with the row id when the row is removed.
 * @param   onCombinatorChange           Invoked with the gap index and next combinator on change.
 * @returns The check-in condition row component.
 */
const CanRequestCheckinConditionRow = ({
  row,
  index,
  isLast,
  disabled,
  typeLabel,
  removeConditionLabel,
  combinatorLabel,
  typeOptions,
  conditionValueLabels,
  vehicleDiscoveryStateOptions,
  combinatorOptions,
  combinatorValue,
  onTypeChange,
  onValueChange,
  onRemove,
  onCombinatorChange
}: CanRequestCheckinConditionRowProps) => {
  const classes = useStyles();

  const onTypeSelectChange = useCallback((event: any) => {
    onTypeChange(row.rowId, Number(event?.target?.value) as CheckinConditionType);
  }, [onTypeChange, row.rowId]);

  const onValueFieldChange = useCallback((event: any) => {
    onValueChange(row.rowId, event?.target?.value ?? '');
  }, [onValueChange, row.rowId]);

  const onRemoveClick = useCallback(() => {
    onRemove(row.rowId);
  }, [onRemove, row.rowId]);

  const onCombinatorSelectChange = useCallback((event: any) => {
    onCombinatorChange(index, event?.target?.value);
  }, [onCombinatorChange, index]);

  return (
    <React.Fragment>
      <div className={classes.row}>
        <div className={classes.typeField}>
          <Dropdown options={typeOptions}
            value={String(row.type)}
            autoComplete={false}
            fieldLabel={typeLabel}
            disabled={disabled}
            onChange={onTypeSelectChange}
            labelAsValue={false} />
        </div>
        <div className={classes.valueField}>
          <Render>
            <Render.If condition={isVehicleDiscoveryCondition(row.type)}>
              <Dropdown options={vehicleDiscoveryStateOptions}
                value={row.value}
                autoComplete={false}
                fieldLabel={conditionValueLabels[row.type]}
                hasError={!!row.errors?.value}
                errorText={translate(getCheckinConditionValueErrorKey(row))}
                disabled={disabled}
                onChange={onValueFieldChange}
                labelAsValue={false} />
            </Render.If>
            <Render.Else>
              <FormNumberField fieldLabel={conditionValueLabels[row.type]}
                disabled={disabled}
                name={`checkinConditionValue-${row.rowId}`}
                onChange={onValueFieldChange}
                hasError={!!row.errors?.value}
                errorText={translate(getCheckinConditionValueErrorKey(row))}
                value={row.value}
                min={CHECKIN_CONDITION_MIN_VALUE} />
            </Render.Else>
          </Render>
        </div>
        <IconButton icon={<DeleteIcon size={24} />}
          className={classes.removeButton}
          padding={'8px'}
          disabled={disabled}
          aria-label={removeConditionLabel}
          onClick={onRemoveClick} />
      </div>
      <Render.If condition={!isLast}>
        <div className={classes.combinatorRow}>
          <div className={classes.combinatorField}>
            <Dropdown options={combinatorOptions}
              value={combinatorValue}
              autoComplete={false}
              fieldLabel={combinatorLabel}
              disabled={disabled}
              onChange={onCombinatorSelectChange}
              labelAsValue={false} />
          </div>
        </div>
      </Render.If>
    </React.Fragment>
  );
};

export default CanRequestCheckinConditionRow;
